import { icon } from "Services/AssetManagementService";
import { getLogger } from "Services/LoggingService";

const logger = getLogger("Browser notifications");

const activeNotifications: Record<string, Notification> = {};

// if task auto accept is on, we create a notification and immediately dismiss it
// this can cause problems on some operating systems and we need to delay the closing
// https://developer.mozilla.org/en-US/docs/Web/API/Notifications_API/Using_the_Notifications_API#closing_notifications
const NOTIFICATION_CLOSE_TIMEOUT = 1000;

// request notification permission if not already granted
export function requestBrowserNotificationPermission(): void {
    if (Notification.permission === "default") {
        Notification.requestPermission();
    }
}

type BrowserNotification = {
    // it's not required for a notification to have a unique id
    // but at least for now it will always have one
    // because we're associating each notification with a task (reservation)
    // so that we can dismiss the notification when the task (reservation) goes away
    id: string;
    title: string;
    body: string;
    onClick?: () => void;
};

export function showBrowserNotification({ id, title, body, onClick }: BrowserNotification): void {
    if (document.hidden && Notification.permission === "granted") {
        const notification = new Notification(title, {
            body,
            tag: id,
            requireInteraction: true, // keep the notification open until the user interacts with it
            icon: icon("phone-button"), // show a phone icon on the right side of the notification
        });

        activeNotifications[id] = notification;
        // add a click event listener to close the notification
        notification.onclick = () => {
            // bring the browser window to the foreground if it's minimized
            // switch to the correct tab if the user is on a different tab
            // request user attention to the application
            window.focus();

            // run the custom handler
            onClick?.();

            closeBrowserNotification(id);
        };

        logger.log("Showing browser notification for", { taskSid: id });
    }
}

// close a notification by id
export function closeBrowserNotification(id: string): void {
    const notification = activeNotifications[id];
    if (notification) {
        setTimeout(() => {
            // close the notification if it's already visible
            notification.close();

            // corner case
            // the notification is not yet visible (possibly due to the slide in animation)
            // but we already received an event to close it (for example an SSE event)
            // so we're scheduling the closing after the notification will be visible
            notification.addEventListener("show", () => notification.close());

            delete activeNotifications[id];

            logger.log("Closing browser notification for", { taskSid: id });
        }, NOTIFICATION_CLOSE_TIMEOUT);
    }
}

// close all active notifications (e.g., on page unload)
export function closeAllBrowserNotifications(reason: string) {
    const allBrowserNotificationsIds = Object.keys(activeNotifications);

    if (allBrowserNotificationsIds.length) {
        logger.log(`Closing all browser notifications (${reason})`, { taskSids: allBrowserNotificationsIds });
    }

    allBrowserNotificationsIds.forEach(closeBrowserNotification);
}

export function initializeBrowserNotifications() {
    requestBrowserNotificationPermission();

    // add an event listener to clear notifications on page unload
    window.addEventListener("beforeunload", () => closeAllBrowserNotifications("Page unload"));

    // add an event listener to clear notifications on tab focus
    document.addEventListener("visibilitychange", () => {
        // When tab becomes visible (selected)
        if (!document.hidden) {
            // Close all notifications
            closeAllBrowserNotifications("Tab visible");
        }
    });
}
